import './updated_by_user_notification.scss';

/*============================================================================*/
/*===============  UPDATED BY USER NOTIFICATION DIRECTIVE  ===================*/
/*============================================================================*/
/*
 * Element directive: <updated-by-user-notification>
 *
 * PURPOSE
 * Displays a floating user avatar near any element that was just updated by
 * a collaborator. The avatar appears for 3 seconds then fades out.
 *
 * HOW IT IS TRIGGERED
 * Any part of the app can signal an update by calling:
 *
 *   gudhub.emit('display_avatar_on_item_updated_by_user', {}, { element, user_id });
 *   gudhub.emit('display_avatar_on_value_updated_by_user', {}, { element, user_id });
 *
 * Current emitters:
 *   - gh_elements/task_board/task_board.js  →  'display_avatar_on_item_updated_by_user'
 *     Fires when gudhub emits 'gh_item_update' for a task-board card.
 *
 *   - core/gh_element/gh_element.js         →  'display_avatar_on_value_updated_by_user'
 *     Fires when gudhub emits 'gh_value_update' for a single field element.
 *
 * EVENT PAYLOAD  { element: DOMElement, user_id: string|number }
 *   element  — the DOM node to anchor the avatar to (uses getBoundingClientRect)
 *   user_id  — looked up via gudhub.getUserById to get fullname / avatar URL
 */

angular.module('updatedByUserNotificationModule', [])
.directive('updatedByUserNotification', ['$timeout', '$compile', function($timeout, $compile) {
    return {
        restrict: 'E',
        template: '<div class="updated-by-user-notification-overlay"></div>',
        link: function($scope, $element) {
            const overlay = $element[0].querySelector('.updated-by-user-notification-overlay');

            //---------- AVATAR EVENT HANDLER WITH DEBOUNCE ----------//
            // A single user action (e.g. editing a field in a task-board card) can fire
            // two events almost simultaneously:
            //   1. 'display_avatar_on_value_updated_by_user' — from gh_element, anchored to
            //      the specific field element (most precise position).
            //   2. 'display_avatar_on_item_updated_by_user'  — from task_board, fired ~500ms
            //      later after ng-repeat re-renders, anchored to the whole card.
            //
            // To avoid showing two avatars for the same action we debounce per user_id:
            //   - All events from the same user within the debounce window are collected.
            //   - Each new event resets the timer (trailing-edge debounce).
            //   - At expiry, resolveEvents() decides which event(s) to actually render.
            const pendingAvatars = new Map(); // user_id → { timerId, events: [{ eventName, data }] }

            // Tracks the avatar currently on screen for each user_id so a new event
            // for the same user relocates it instead of stacking a duplicate.
            const activeAvatars = new Map(); // user_id → { el, scope, removeTimer }

            const DISPLAY_DURATION_MS = 3000; // how long an avatar stays fully visible
            const LEAVE_DURATION_MS = 350; // must match the .is-leaving animation-duration in the SCSS

            function handleAvatarEvent(eventName, data) {
                if (!data || !data.element || !data.user_id) return;

                let pending = pendingAvatars.get(data.user_id);
                if (pending) {
                    // Another event for the same user arrived within the debounce window —
                    // reset the timer and append this event to the collection.
                    $timeout.cancel(pending.timerId);
                    pending.events.push({ eventName, data });
                } else {
                    pending = { events: [{ eventName, data }], timerId: null };
                    pendingAvatars.set(data.user_id, pending);
                }

                pending.timerId = $timeout(function() {
                    pendingAvatars.delete(data.user_id);
                    resolveEvents(pending.events);
                }, 300);
            }

            // Decides which collected events to render after the debounce window closes.
            // Priority rule: if both item and value events arrived for the same user,
            // only the value event(s) are shown — their element anchor is more precise
            // (the specific field) vs the item event's whole-card anchor.
            // If only one event type arrived (e.g. drag-and-drop → item only), show it.
            function resolveEvents(events) {
                const hasItem  = events.some(function(e) { return e.eventName === 'display_avatar_on_item_updated_by_user'; });
                const hasValue = events.some(function(e) { return e.eventName === 'display_avatar_on_value_updated_by_user'; });

                if (hasItem && hasValue) {
                    events
                        .filter(function(e) { return e.eventName === 'display_avatar_on_value_updated_by_user'; })
                        .forEach(function(e) { showAvatar(e.data); });
                } else {
                    events.forEach(function(e) { showAvatar(e.data); });
                }
            }

            // Starts the fade-out for user_id's avatar once its display window ends, then
            // removes the DOM node/scope once the fade-out animation finishes. The entry is
            // kept in activeAvatars for the whole fade-out so a move event arriving mid-fade
            // can still cancel it (see showAvatar's move branch) instead of losing the avatar.
            function scheduleRemoval(user_id) {
                return $timeout(function() {
                    const active = activeAvatars.get(user_id);
                    if (!active) return;

                    active.el.classList.add('is-leaving');
                    active.removeTimer = $timeout(function() {
                        if (active.el.parentNode) {
                            active.el.parentNode.removeChild(active.el);
                        }
                        active.scope.$destroy();
                        activeAvatars.delete(user_id);
                    }, LEAVE_DURATION_MS);
                }, DISPLAY_DURATION_MS);
            }

            function showAvatar(data) {
                const rect = data.element.getBoundingClientRect();

                gudhub.getUserById(data.user_id).then(function(user) {
                    const name = user ? (user.fullname || '') : '';
                    const url = user ? (user.avatar_128 || user.avatar_512 || '') : '';

                    const active = activeAvatars.get(data.user_id);
                    if (active) {
                        // Avatar is still visible (or fading out) from a previous event for
                        // this user — move it to the new position instead of creating a
                        // duplicate. The CSS transition on top/left animates the slide.
                        $timeout.cancel(active.removeTimer);
                        active.el.classList.remove('is-leaving');

                        active.el.style.top = `${rect.top}px`;
                        active.el.style.left = `${rect.left}px`;

                        active.removeTimer = scheduleRemoval(data.user_id);
                        return;
                    }

                    const urlAttr = url ? ` url="${url}"` : '';
                    const html = `<div class="updated-by-user-notification-item" style="top: ${rect.top}px; left: ${rect.left}px;">
                        <gh-avatar-webcomponent name="${name}"${urlAttr}></gh-avatar-webcomponent>
                    </div>`;

                    const itemScope = $scope.$new(true);
                    const item = $compile(html)(itemScope)[0];

                    overlay.appendChild(item);

                    const entry = { el: item, scope: itemScope, removeTimer: null };
                    activeAvatars.set(data.user_id, entry);
                    entry.removeTimer = scheduleRemoval(data.user_id);
                });
            }

            gudhub.on('display_avatar_on_item_updated_by_user display_avatar_on_value_updated_by_user', {}, handleAvatarEvent, $scope);

            $scope.$on('$destroy', function() {
                gudhub.destroy('display_avatar_on_item_updated_by_user display_avatar_on_value_updated_by_user', {}, handleAvatarEvent);

                pendingAvatars.forEach(function(pending) {
                    $timeout.cancel(pending.timerId);
                });
                pendingAvatars.clear();

                activeAvatars.forEach(function(active) {
                    $timeout.cancel(active.removeTimer);
                });
                activeAvatars.clear();
            });
        }
    };
}]);
